import { useState, useEffect, useMemo } from "react"
import { intersect, featureCollection } from "@turf/turf"
import type { Feature, FeatureCollection, Polygon, MultiPolygon } from "geojson"
import { getPLUPartitionAreas } from "@/lib/explore/api/plu"
import type { ICommune } from "@/types/explore"

interface LayerFilters {
  plu?: Record<string, unknown>
  [key: string]: Record<string, unknown> | undefined
}

function groupByPrefix(arr: string[]): string[] {
  const result: string[] = []
  arr.forEach((item) => {
    if (item.startsWith("U")) {
      if (/^U[A-Z]$/.test(item)) {
        result.push(item)
      } else if (/^U[A-Z].*/.test(item)) {
        const prefix = item.slice(0, 2)
        if (!result.includes(prefix)) result.push(prefix)
      }
    } else {
      result.push(item)
    }
  })
  return result
}

export const usePLUData = (selectedCommune: ICommune | undefined, layerFilters?: LayerFilters) => {
  const [pluData, setPluData] = useState<Feature[]>([])

  useEffect(() => {
    const partition = selectedCommune?.pluDocument?.partition
    if (!partition) {
      setPluData([])
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const pluAreas = await getPLUPartitionAreas(partition)
        if (cancelled) return
        if (selectedCommune?.contour) {
          const communeFeature = {
            type: "Feature" as const,
            properties: {},
            geometry: selectedCommune.contour,
          } as Feature
          const clipped = pluAreas.features.reduce((acc: Feature[], f: Feature) => {
            const clippedGeom = intersect(
              featureCollection([f, communeFeature]) as FeatureCollection<Polygon | MultiPolygon>,
            )
            if (clippedGeom) acc.push({ ...f, geometry: clippedGeom.geometry })
            return acc
          }, [])
          setPluData(clipped)
        } else {
          setPluData(pluAreas.features as Feature[])
        }
      } catch (error) {
        console.error("Error fetching PLU data:", error)
        if (!cancelled) setPluData([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [selectedCommune?.pluDocument?.partition])

  const pluTypeZones = useMemo(
    () =>
      (layerFilters?.plu?.["plu-zones"] as string[] | undefined)?.length
        ? (layerFilters!.plu!["plu-zones"] as string[])
        : ["U", "AUc", "AUs", "N", "A"],
    [layerFilters?.plu?.["plu-zones"]],
  )

  const pluOpacity = useMemo(
    () => (layerFilters?.plu?.["plu-opacity"] != null ? (layerFilters.plu["plu-opacity"] as number) / 100 : 0.5),
    [layerFilters?.plu?.["plu-opacity"]],
  )

  const hasExplicitZones = !!(layerFilters?.plu?.["plu-zones"] as string[] | undefined)?.length

  const pluSubzoneOptions = useMemo(() => {
    if (!hasExplicitZones) return []
    const libelles = pluData
      .filter((f: Feature) => pluTypeZones.includes(f.properties?.typezone))
      .map((f: Feature) => f.properties?.libelle as string)
      .filter(Boolean)
    return [...new Set(groupByPrefix(libelles))].sort((a, b) => a.localeCompare(b))
  }, [pluData, pluTypeZones, hasExplicitZones])

  const pluLibelles = useMemo(
    () => (layerFilters?.plu?.["plu-subzones"] as string[] | undefined) ?? [],
    [layerFilters?.plu?.["plu-subzones"]],
  )

  return { pluData, pluTypeZones, pluOpacity, pluSubzoneOptions, pluLibelles }
}
